import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Church, MapPin, Search, SlidersHorizontal, Tag, X } from 'lucide-react';
import GroupCard from './GroupCard';
import { useAuth0 } from '@auth0/auth0-react';
import { StatusMessage } from '../ui/StatusMessage';
import '../../styles/components/FetchGroups.css';

export const FetchGroups = () => {
  const signalParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search || '');
    const tags = (params.get('tags') || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    return {
      intent: params.get('intent') || '',
      signalTitle: params.get('signalTitle') || '',
      signalType: params.get('signalType') || '',
      location: params.get('location') || '',
      tags,
    };
  }, []);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    category: '',
    keywords: [signalParams.signalTitle, signalParams.signalType, ...signalParams.tags].filter(Boolean).join(' '),
    city: '',
    state: '',
    postal: '',
  });

  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    document.title = 'Explore Communities | ThousandYearKingdom.org';
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const populateGroupsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        });
        const response = await fetch(`${apiUrl}/api/GroupsTbls`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Communities could not be loaded (${response.status}).`);
        }

        const data = await response.json();
        setGroups(Array.isArray(data)
          ? data.filter((group) => !isGeographicGroup(group))
          : []);
      } catch (err) {
        setError(err.message || 'Communities could not be loaded.');
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };
    populateGroupsData();
  }, [apiUrl, getAccessTokenSilently]);

  const filteredGroups = useMemo(() => {
    return groups.filter((group) => {
      const matchName =
        filters.name === '' ||
        (group.groupName ?? '').toLowerCase().includes(filters.name.toLowerCase());
      const matchCategory =
        filters.category === '' ||
        (group.groupCategory ?? '').toLowerCase().includes(filters.category.toLowerCase());
      const keywords = `${group.groupDescription ?? ''} ${group.groupMission ?? ''}`.toLowerCase();
      const matchKeywords =
        filters.keywords === '' || keywords.includes(filters.keywords.toLowerCase());
      const matchCity =
        filters.city === '' || (group.city ?? '').toLowerCase().includes(filters.city.toLowerCase());
      const matchState =
        filters.state === '' || (group.state ?? '').toLowerCase().includes(filters.state.toLowerCase());
      const matchPostal =
        filters.postal === '' ||
        (group.postalCode ?? '').toLowerCase().includes(filters.postal.toLowerCase());

      return (
        matchName &&
        matchCategory &&
        matchKeywords &&
        matchCity &&
        matchState &&
        matchPostal
      );
    });
  }, [groups, filters]);

  const hasActiveFilters = Object.values(filters).some((value) => String(value).trim().length > 0);

  const clearFilters = () => setFilters({
    name: '',
    category: '',
    keywords: '',
    city: '',
    state: '',
    postal: '',
  });

  const renderGroupCards = (groupsToRender) => {
    return groupsToRender.map((group) => (
      <article key={group.id} className="community-result-card">
        <GroupCard group={group} />
      </article>
    ));
  };

  return (
    <main className="community-explorer-shell">
      <header className="community-explorer-heading">
        <p>Find your people</p>
        <h1>Explore Communities</h1>
        <span>Search by purpose, category, or location to find groups where you can belong and contribute.</span>
      </header>

      <section className="community-connect-callout" aria-labelledby="community-connect-title">
        <div className="community-connect-icon" aria-hidden="true"><Church size={25} /></div>
        <div className="community-connect-copy">
          <h2 id="community-connect-title">Help your community become discoverable</h2>
          <p>Connect an existing church, ministry, or organization—or gather people around a new shared mission. Each community keeps its own identity and leadership.</p>
        </div>
        <a href="/post-group" className="community-connect-action">
          Connect your church or start a community <ArrowRight size={18} />
        </a>
      </section>

      {signalParams.signalTitle && (
        <div
          className="mb-5 rounded-xl p-4"
          style={{ background: 'rgba(250, 204, 21, 0.12)', border: '1px solid rgba(250, 204, 21, 0.28)' }}
        >
          <p className="mb-1 text-xs font-bold uppercase tracking-[0.18em] text-yellow-300">Mission Signal</p>
          <h2 className="text-2xl font-bold text-white mb-1">{signalParams.signalTitle}</h2>
          <p className="text-white/80 mb-0">
            {signalParams.intent === 'create-idea'
              ? 'Choose a colony that should host this idea, or start a new one if none fits.'
              : 'Browse colonies whose mission may overlap with this signal.'}
          </p>
          {signalParams.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {signalParams.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-black/30 px-2.5 py-1 text-xs font-semibold text-yellow-100">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <section className="community-search-panel" aria-label="Community search filters">
        <div className="community-search-panel-heading">
          <div>
            <span><SlidersHorizontal size={17} /> Search and filter</span>
            <p>Narrow the list without leaving the page.</p>
          </div>
          {hasActiveFilters && (
            <button type="button" className="community-clear-filters" onClick={clearFilters}>
              <X size={16} /> Clear all
            </button>
          )}
        </div>

        <div className="community-filter-grid">
          <label className="community-filter-field community-filter-wide" htmlFor="community-filter-name">
            <span><Search size={16} /> Community name</span>
            <input
              id="community-filter-name"
              type="text"
              placeholder="Try RiverLife, food ministry, neighborhood..."
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
            />
          </label>

          <label className="community-filter-field" htmlFor="community-filter-category">
            <span><Tag size={16} /> Category</span>
            <select id="community-filter-category" name="category" value={filters.category} onChange={handleFilterChange}>
              <option value="">All categories</option>
              <option value="Charity">Charity & Aid</option>
              <option value="Environmental">Environmental</option>
              <option value="Education">Education & Literacy</option>
              <option value="Farming">Farming & Gardening</option>
              <option value="Technology">Technology & AI</option>
              <option value="Business">Business & Trade</option>
              <option value="Crafts">Crafts & Artisanship</option>
              <option value="Services">Labor & Services</option>
              <option value="Faith">Faith & Fellowship</option>
              <option value="Health">Health & Wellness</option>
              <option value="Defense">Defense & Security</option>
              <option value="Media">Media & Journalism</option>
              <option value="Housing">Housing & Shelter</option>
              <option value="Governance">Coordination & Policy</option>
              <option value="Finance">Finance & Treasury</option>
              <option value="Hospitality">Food & Hospitality</option>
            </select>
          </label>

          <label className="community-filter-field" htmlFor="community-filter-keywords">
            <span><Search size={16} /> Mission keywords</span>
            <input
              id="community-filter-keywords"
              type="text"
              placeholder="Outreach, farming, worship..."
              name="keywords"
              value={filters.keywords}
              onChange={handleFilterChange}
            />
          </label>

          <label className="community-filter-field" htmlFor="community-filter-city">
            <span><MapPin size={16} /> City</span>
            <input
              id="community-filter-city"
              type="text"
              placeholder="Youngstown"
              name="city"
              value={filters.city}
              onChange={handleFilterChange}
            />
          </label>

          <label className="community-filter-field" htmlFor="community-filter-state">
            <span>State / region</span>
            <input
              id="community-filter-state"
              type="text"
              placeholder="New York"
              name="state"
              value={filters.state}
              onChange={handleFilterChange}
            />
          </label>

          <label className="community-filter-field" htmlFor="community-filter-postal">
            <span>Postal code</span>
            <input
              id="community-filter-postal"
              type="text"
              placeholder="14174"
              name="postal"
              value={filters.postal}
              onChange={handleFilterChange}
            />
          </label>
        </div>
      </section>

      {!loading && !error && (
        <div className="community-results-summary" aria-live="polite">
          <strong>{filteredGroups.length}</strong>
          <span>{filteredGroups.length === 1 ? 'community found' : 'communities found'}</span>
        </div>
      )}

      {loading ? (
        <StatusMessage tone="loading" title="Loading communities" message="Please wait while we fetch available communities." />
      ) : error ? (
        <StatusMessage tone="error" title="Could not load communities" message={error} />
      ) : filteredGroups.length === 0 ? (
        <StatusMessage tone="empty" title="No communities found" message="Try changing your filters or check back later." />
      ) : (
        <section className="community-results-grid" aria-label="Community search results">
          {renderGroupCards(filteredGroups)}
        </section>
      )}
    </main>
  );
};

const isGeographicGroup = (group) => (
  (group.groupType ?? '').toLowerCase() === 'geographic' ||
  Boolean(group.geoKey)
);
